import axios from 'axios';
import firebase from '../config/firebase.config';
import { FHIR_API_BASE_URL } from '../config/healthcare-api-config';

// Create axios instance with default config
const fhirApi = axios.create({
  baseURL: FHIR_API_BASE_URL,
  headers: {
    'Content-Type': 'application/fhir+json',
    'Accept': 'application/fhir+json'
  }
});

// Add request interceptor for authentication
fhirApi.interceptors.request.use(
  async (config) => {
    try {
      // Get the current user's ID token
      const user = firebase.auth().currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      console.error('Error in request interceptor:', error);
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
fhirApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      console.error('Unauthorized access to FHIR API');
      // Redirect to login if needed
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      // Handle forbidden access (HIPAA compliance)
      console.error('Forbidden access to PHI data');
      // Log the incident for audit
      console.error('HIPAA Compliance Alert: Unauthorized PHI access attempt');
    }
    return Promise.reject(error);
  }
);

// FHIR Resource Methods
const fhirApiService = {
  // Patient Resource
  getPatient: async (id) => {
    try {
      const response = await fhirApi.get(`/Patient/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching patient:', error);
      throw error;
    }
  },

  searchPatients: async (params) => {
    try {
      const response = await fhirApi.get('/Patient', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching patients:', error);
      throw error;
    }
  },

  // Encounter Resource
  getEncounter: async (id) => {
    try {
      const response = await fhirApi.get(`/Encounter/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching encounter:', error);
      throw error;
    }
  },

  searchEncounters: async (params) => {
    try {
      const response = await fhirApi.get('/Encounter', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching encounters:', error);
      throw error;
    }
  },

  // Observation Resource
  getObservation: async (id) => {
    try {
      const response = await fhirApi.get(`/Observation/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching observation:', error);
      throw error;
    }
  },

  searchObservations: async (params) => {
    try {
      const response = await fhirApi.get('/Observation', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching observations:', error);
      throw error;
    }
  },

  // Add new method for clinical sidebar data
  getPatientClinicalData: async (patientId) => {
    try {
      const [patient, encounters, observations] = await Promise.all([
        this.getPatient(patientId),
        this.searchEncounters({ patient: patientId }),
        this.searchObservations({ patient: patientId })
      ]);

      return {
        patient,
        encounters: encounters.entry?.map(e => e.resource) || [],
        observations: observations.entry?.map(e => e.resource) || []
      };
    } catch (error) {
      console.error('Error fetching clinical data:', error);
      throw error;
    }
  }
};

// Export both the service and the axios instance for testing
export { fhirApiService, fhirApi };
export default fhirApiService; 